export interface CampaignResponse {
    user_guid: string;
    user_name?: string | null;
    status: 'pending' | 'accepted' | 'rejected';
    school_guid?: string | null;
    school_name?: string | null;
    selected_books?: string[] | null;
    responded_at?: string | null;
}

export interface CampaignBook {
    isbn: string;
    title: string;
}

export interface CampaignDailyCounts {
    accepted: number;
    rejected: number;
    responded: number;
    selected_books: number;
    book_counts: Record<string, number>;
}

export interface CampaignDailyRow extends CampaignDailyCounts {
    date: string;
}

const isCampaignResponse = (value: unknown): value is CampaignResponse => {
    if (!value || typeof value !== 'object') return false;
    if (
        !('user_guid' in value) ||
        typeof value.user_guid !== 'string' ||
        !('status' in value) ||
        !['pending', 'accepted', 'rejected'].includes(String(value.status))
    ) return false;
    for (const key of ['user_name', 'school_guid', 'school_name']) {
        if (key in value && value[key] != null && typeof value[key] !== 'string') return false;
    }
    if ('selected_books' in value && value.selected_books != null && (
        !Array.isArray(value.selected_books) ||
        !value.selected_books.every(isbn => typeof isbn === 'string')
    )) return false;
    if (value.status === 'pending') return true;
    return 'responded_at' in value &&
        typeof value.responded_at === 'string' &&
        Number.isFinite(Date.parse(value.responded_at)) &&
        (value.status !== 'accepted' || (
            'selected_books' in value &&
            Array.isArray(value.selected_books) &&
            value.selected_books.length > 0
        ));
};

export const parseCampaignResponses = (data: unknown): CampaignResponse[] => {
    if (!Array.isArray(data) || !data.every(isCampaignResponse)) {
        throw new Error('Invalid campaign response data');
    }
    return data;
};

export const buildCampaignDailyReport = (
    responses: CampaignResponse[],
    campaignBooks: CampaignBook[]
) => {
    const booksByIsbn = new Map(campaignBooks.map(book => [book.isbn, book]));
    for (const response of responses) {
        if (response.status !== 'accepted') continue;
        for (const isbn of response.selected_books || []) {
            if (!booksByIsbn.has(isbn)) booksByIsbn.set(isbn, { isbn, title: '' });
        }
    }
    const books = Array.from(booksByIsbn.values());
    const emptyCounts = (): CampaignDailyCounts => ({
        accepted: 0,
        rejected: 0,
        responded: 0,
        selected_books: 0,
        book_counts: Object.fromEntries(books.map(book => [book.isbn, 0])),
    });
    const days = new Map<string, CampaignDailyRow>();
    const totals = emptyCounts();
    for (const response of responses) {
        if (response.status === 'pending') continue;
        if (!response.responded_at || !Number.isFinite(Date.parse(response.responded_at))) {
            throw new Error('Campaign response is missing a valid timestamp');
        }
        const date = new Date(response.responded_at).toLocaleDateString('sv-SE', {
            timeZone: 'Europe/Helsinki',
        });
        let day = days.get(date);
        if (!day) {
            day = { date, ...emptyCounts() };
            days.set(date, day);
        }
        const selected = response.status === 'accepted'
            ? Array.from(new Set(response.selected_books || []))
            : [];
        for (const counts of [day, totals]) {
            counts[response.status]++;
            counts.responded++;
            counts.selected_books += selected.length;
            for (const isbn of selected) counts.book_counts[isbn]++;
        }
    }
    return {
        books,
        days: Array.from(days.values()).sort((a, b) => a.date.localeCompare(b.date)),
        totals,
    };
};
